import React, { useState } from 'react';
import './FilePreview.css';

const FilePreview = ({ file }) => {
  const [imageError, setImageError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!file) return null;

  const getFileIcon = (mimetype, filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();

    // Check by extension first for better accuracy
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return { icon: '🖼️', color: '#4a9eff' };
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(ext)) {
      return { icon: '🎬', color: '#e01e5a' };
    }
    if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(ext)) {
      return { icon: '🎵', color: '#7c3085' };
    }
    if (ext === 'pdf') {
      return { icon: '📄', color: '#d72b3f' };
    }
    if (['doc', 'docx'].includes(ext)) {
      return { icon: '📝', color: '#2e7eee' };
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return { icon: '📊', color: '#1d9e58' };
    }
    if (['ppt', 'pptx'].includes(ext)) {
      return { icon: '📽️', color: '#f4811f' };
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return { icon: '📦', color: '#955f20' };
    }
    if (['txt', 'md', 'json', 'xml', 'log'].includes(ext)) {
      return { icon: '📃', color: '#666' };
    }
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'go', 'rs'].includes(ext)) {
      return { icon: '💻', color: '#4a9eff' };
    }

    // Fallback to mimetype checking
    if (mimetype?.startsWith('image/')) return { icon: '🖼️', color: '#4a9eff' };
    if (mimetype?.startsWith('video/')) return { icon: '🎬', color: '#e01e5a' };
    if (mimetype?.startsWith('audio/')) return { icon: '🎵', color: '#7c3085' };

    return { icon: '📎', color: '#666' };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Check file type - Slack uses 'filetype' property, not 'mimetype'
  const ext = (file.name || file.title)?.split('.').pop()?.toLowerCase();
  const fileType = file.filetype || file.mimetype || '';
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const isImage = fileType.startsWith('image/') || imageTypes.includes(ext) || imageTypes.includes(fileType);
  const isPDF = fileType.includes('pdf') || ext === 'pdf';
  const isVideo = fileType.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext);
  const isAudio = fileType.startsWith('audio/') || ['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(ext);

  // Get the best image URL - use proxy for Slack private URLs
  const getImageUrl = () => {
    // For local preview (optimistic upload)
    if (file.preview) return file.preview;

    // Get the best available Slack URL
    const slackUrl = file.thumb_480 || file.thumb_360 || file.thumb_160 || file.thumb_80 || file.url_private;

    if (!slackUrl) return null;

    // If it's a Slack URL, use the proxy to handle authentication
    if (slackUrl.includes('slack.com') || slackUrl.includes('slack-files.com')) {
      const token = localStorage.getItem('slack_user_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      return `${backendUrl}/api/files/proxy?url=${encodeURIComponent(slackUrl)}&token=${encodeURIComponent(token)}`;
    }

    return slackUrl;
  };

  const renderPreview = () => {
    if (isImage && !imageError) {
      const imageUrl = getImageUrl();
      if (!imageUrl) return null;

      return (
        <div
          className="file-image-preview"
          onClick={() => setExpanded(!expanded)}
        >
          <img
            src={imageUrl}
            alt={file.name || file.title}
            onError={() => setImageError(true)}
            className={expanded ? 'expanded' : ''}
          />
        </div>
      );
    }

    if (isPDF && file.thumb_pdf) {
      return (
        <div className="file-pdf-preview">
          <img
            src={file.thumb_pdf}
            alt={file.name}
            className="pdf-thumbnail"
          />
        </div>
      );
    }

    if (isVideo && file.thumb_video) {
      return (
        <div className="file-video-preview">
          <img
            src={file.thumb_video}
            alt={file.name}
            className="video-thumbnail"
          />
          <div className="video-play-overlay">▶</div>
        </div>
      );
    }

    return null;
  };

  const fileIcon = getFileIcon(file.mimetype, file.name || file.title);

  return (
    <div className={`file-preview ${expanded ? 'expanded' : ''}`}>
      <div className="file-header">
        <div className="file-icon-wrapper" style={{ backgroundColor: `${fileIcon.color}15` }}>
          <span className="file-icon" style={{ fontSize: '20px' }}>{fileIcon.icon}</span>
        </div>
        <div className="file-info">
          <a
            href={file.url_private || file.url_private_download}
            target="_blank"
            rel="noopener noreferrer"
            className="file-name"
          >
            {file.name || file.title}
          </a>
          <div className="file-meta">
            <span className="file-size">{formatFileSize(file.size)}</span>
            <span className="file-type-separator">•</span>
            <span className="file-type">
              {file.mimetype?.split('/')[1]?.toUpperCase() ||
               file.name?.split('.').pop()?.toUpperCase() ||
               'FILE'}
            </span>
            {file.user && (
              <>
                <span className="file-type-separator">•</span>
                <span className="file-uploader">Uploaded by {file.user}</span>
              </>
            )}
          </div>
        </div>
        <div className="file-actions">
          <button
            className="file-action-btn"
            onClick={() => window.open(file.url_private || file.url_private_download, '_blank')}
            title="Open in new tab"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M10.5 8.5V11.5C10.5 12 10 12.5 9.5 12.5H2.5C2 12.5 1.5 12 1.5 11.5V4.5C1.5 4 2 3.5 2.5 3.5H5.5M8.5 1.5H12.5M12.5 1.5V5.5M12.5 1.5L7 7"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <a
            href={file.url_private_download || file.url_private}
            download={file.name}
            className="file-action-btn"
            title="Download"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1V9M7 9L4 6M7 9L10 6M1 12H13"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>

      {renderPreview()}
    </div>
  );
};

export default FilePreview;